import { useCallback, useMemo } from "react";

import { EventOrigin } from "apis/procore";
import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useLazyHidden } from "contexts/hidden";
import { CORE_TOOLBAR_IDS } from "contexts/toolbars";
import { CORE_WINDOW_IDS, useDispatchWindows, windowsActions } from "contexts/windows";
import { useResetView } from "features/home/useResetView";
import { toggleOrthographicProjection } from "features/orthoCam/toggleOrthographicProjection";
import { selectCameraType, selectMainObject, selectMainObjectPick } from "features/render";
import { useToggleXRayMode } from "features/render/hooks/useToggleXRayMode";
import { useSelectionActions } from "features/selectionBar/useSelectionActions";
import { useConfirmCurrentModeChanges } from "features/toolMode/useConfirmCurrentModeChanges";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { runRedo, runUndo } from "features/undoRedo";
import {
    clearAllMeasurements,
    clearAllSectionPlanes,
    resetMeasureTool,
} from "features/viewModifications/clearModifications";
import { useHandlePickerEsc } from "hooks/useHandlePickerEsc";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";
import { DEFAULT_MEASURE_PICKER, DEFAULT_SECTION_PICKER } from "slices/procoreUx/utils";
import { unhideLastHiddenObjects } from "utils/objectActions";

import { SPACE_MOUSE_COMMANDS, type SpaceMouseCommandHandlers } from "./applicationCommands";

/** Composes the same actions used by the Procore toolbars and keyboard shortcuts. */
export function useSpaceMouseCommands(): SpaceMouseCommandHandlers {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const dispatchWindows = useDispatchWindows();
    const dispatchHidden = useDispatchHidden();
    const hidden = useLazyHidden();
    const enterToolMode = useEnterToolMode();
    const confirmCurrentModeChanges = useConfirmCurrentModeChanges();
    const cancelCurrentModeChanges = useHandlePickerEsc();
    const resetView = useResetView();
    const toggleXRayMode = useToggleXRayMode();
    // Hide, Isolate, Zoom to selected, and Deselect all are the SelectionBar's buttons, so they must
    // do what those do. The hook owns the one useFlyToSelected instance this component needs.
    const selection = useSelectionActions(EventOrigin.SpaceMouse);
    const {
        state: { view, scene, dataAccess },
    } = useExplorerGlobals();

    const toggleWindow = useCallback(
        (id: (typeof CORE_WINDOW_IDS)[keyof typeof CORE_WINDOW_IDS]) => {
            dispatchWindows(
                windowsActions.toggleWindow(id, {
                    elementId: CORE_TOOLBAR_IDS.DETAILS,
                    placement: "right",
                }),
            );
        },
        [dispatchWindows],
    );

    // A disabled SelectionBar button ignores the click; the puck command does the same.
    const { hide, isolate, zoomToSelection, deselectAll, deselectAllDisabled } = selection;
    const hideSelected = useCallback(() => {
        if (hide && !hide.disabled) {
            hide.onClick();
        }
    }, [hide]);

    const toggleIsolate = useCallback(() => {
        if (isolate && !isolate.disabled) {
            isolate.onClick();
        }
    }, [isolate]);

    const flyToSelected = useCallback(() => {
        if (zoomToSelection && !zoomToSelection.disabled) {
            zoomToSelection.onClick();
        }
    }, [zoomToSelection]);

    const clearSelection = useCallback(() => {
        if (!deselectAllDisabled) {
            deselectAll();
        }
    }, [deselectAll, deselectAllDisabled]);

    const undo = useCallback(() => {
        runUndo(dispatch, store.getState());
    }, [dispatch, store]);

    const redo = useCallback(() => {
        runRedo(dispatch, store.getState());
    }, [dispatch, store]);

    // The flyout's Reset all: every modification at once, whatever tool is active.
    const resetModifications = useCallback(() => {
        void resetView({ resetCamera: false, resetVersionComparisonPropertyTree: false, preservePicker: true });
    }, [resetView]);

    // The flyout only offers Clear measurements in Default mode. Mid-measure this is the reset
    // button's Measure branch, so the X-ray the object-to-object tool turned on goes with the result.
    const clearMeasurements = useCallback(() => {
        const state = store.getState();
        if (selectActiveToolMode(state) === ProcoreToolMode.Measure) {
            resetMeasureTool(dispatch, state);
            return;
        }
        clearAllMeasurements(dispatch, state);
    }, [dispatch, store]);

    const toggleProjection = useCallback(() => {
        const state = store.getState();
        toggleOrthographicProjection({
            dispatch,
            view,
            scene,
            dataAccess,
            cameraType: selectCameraType(state),
            mainObject: selectMainObject(state),
            mainObjectPick: selectMainObjectPick(state),
            telemetrySource: "useSpaceMouseCommands.toggleProjection",
        });
    }, [dataAccess, dispatch, scene, store, view]);

    return useMemo(
        () => ({
            [SPACE_MOUSE_COMMANDS.Properties]: () => toggleWindow(CORE_WINDOW_IDS.PROPERTIES),
            [SPACE_MOUSE_COMMANDS.ObjectTree]: () => toggleWindow(CORE_WINDOW_IDS.OBJECT_TREE),
            [SPACE_MOUSE_COMMANDS.Measure]: () => enterToolMode({ picker: DEFAULT_MEASURE_PICKER }),
            [SPACE_MOUSE_COMMANDS.Confirm]: confirmCurrentModeChanges,
            [SPACE_MOUSE_COMMANDS.Cancel]: cancelCurrentModeChanges,
            [SPACE_MOUSE_COMMANDS.Section]: () => enterToolMode({ picker: DEFAULT_SECTION_PICKER }),
            [SPACE_MOUSE_COMMANDS.ClearMeasurements]: clearMeasurements,
            [SPACE_MOUSE_COMMANDS.ClearSection]: () => clearAllSectionPlanes(dispatch, store.getState()),
            [SPACE_MOUSE_COMMANDS.Undo]: undo,
            [SPACE_MOUSE_COMMANDS.Redo]: redo,
            [SPACE_MOUSE_COMMANDS.Hide]: hideSelected,
            [SPACE_MOUSE_COMMANDS.UnhideLast]: () =>
                unhideLastHiddenObjects(hidden.current.lastHiddenIds, { dispatch, dispatchHidden }),
            [SPACE_MOUSE_COMMANDS.Isolate]: toggleIsolate,
            [SPACE_MOUSE_COMMANDS.FlyTo]: flyToSelected,
            [SPACE_MOUSE_COMMANDS.ClearSelection]: clearSelection,
            [SPACE_MOUSE_COMMANDS.ClearView]: resetModifications,
            [SPACE_MOUSE_COMMANDS.Home]: () => void resetView(),
            [SPACE_MOUSE_COMMANDS.Ortho]: toggleProjection,
            [SPACE_MOUSE_COMMANDS.XRay]: toggleXRayMode,
        }),
        [
            cancelCurrentModeChanges,
            clearMeasurements,
            clearSelection,
            confirmCurrentModeChanges,
            dispatch,
            dispatchHidden,
            enterToolMode,
            flyToSelected,
            hidden,
            hideSelected,
            redo,
            resetModifications,
            resetView,
            store,
            toggleIsolate,
            undo,
            toggleProjection,
            toggleWindow,
            toggleXRayMode,
        ],
    );
}
